import { LazyLoadComponent } from 'react-lazy-load-image-component'
import { ItemListProps } from './ItemList.types'
import Message from '../Message'
import {
  ItemListCard,
  ItemListGrid,
  ItemListWrapper,
  HeadingWrapper,
  HeadingText,
  HeadingButton,
  ItemPlaceholder,
  ActionText,
  EmptyMessageWrapper
} from './ItemList.styles'
import { ItemStatus } from '../../../interfaces/enums'
import { deepMerge } from '../../../utils/deepMerge'
import { itemListLayout } from './ItemList.defaultProps'
import { SearchEmptyIcon } from '../../assets/searchEmptyIcon'
import useId from '../../hooks/useId'
import { removeOutOfStockProducts } from './ItemList.utils'

const ItemList = ({
  id = 'ItemList',
  className = '',
  styles,
  items: preSanitizedItems = [],
  heading: {
    // it comes here as heading.value
    buttons: headingButtons = [],
    gap: headingGap = 8,
    ...headingProps
  } = {},
  status = ItemStatus.OK,
  itemCard,
  layout = 'default',
  data,
  showEmptyExtraIcon = false,
  'data-testid': testid = 'item-list',
  shouldShowEmptyMessage = true,
  hideOutOfStock,
  ...otherProps
}: ItemListProps) => {
  const items = hideOutOfStock
    ? removeOutOfStockProducts(preSanitizedItems)
    : preSanitizedItems
  const { getTestId } = useId(testid)
  const layoutKey = layout as keyof typeof itemListLayout
  const defaultProps = deepMerge(
    itemListLayout[layoutKey] || itemListLayout.none,
    otherProps
  )
  const {
    gap,
    columns,
    emptyMessage,
    errorMessage,
    loadingMessage,
    actionText,
    actionFunction,
    ...props
  } = defaultProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (event: any, actions: any) => {
    event.preventDefault()
    event.stopPropagation()
    if (actions) {
      actions({
        data
      })
    }
  }

  const showEmptyMessage =
    !items?.length && status === ItemStatus.OK && emptyMessage
  const showActionText =
    actionText !== undefined &&
    actionFunction !== undefined &&
    status !== 'LOADING'

  if (showEmptyMessage && showEmptyExtraIcon) {
    delete emptyMessage?.icon
  }

  return (
    <>
      {!!items?.length && status !== ItemStatus.LOADING && (
        <ItemListWrapper
          id={id}
          className={`CatalogueList ${className}`}
          gap={headingGap}
          sx={styles}
          data-testid={getTestId()}
          {...props}>
          {!!headingProps?.value && (
            <HeadingWrapper
              id={`${id}-heading-wrapper`}
              className="HeadingWrapper"
              data-testid={getTestId('header')}>
              <HeadingText
                id={`${id}-heading-text`}
                className="HeadingText"
                data-testid={getTestId('heading')}
                {...headingProps}
              />
              {headingButtons.map(
                ({ actions: buttonActions, ...buttonProps }, index) => (
                  <HeadingButton
                    key={buttonProps.label}
                    id={`${id}-heading-button-${index}`}
                    className="HeadingButton"
                    onClick={(event) => handleClick(event, buttonActions)}
                    data-testid={getTestId(`header-button-${index}`)}
                    {...buttonProps}
                  />
                )
              )}
            </HeadingWrapper>
          )}
          <ItemListGrid gap={gap} columns={columns}>
            {items?.map((item, index) => {
              const itemKey = item.id || item.sku || `item-${index}`
              return (
                <LazyLoadComponent
                  key={itemKey}
                  placeholder={
                    <ItemPlaceholder horizontal={itemCard?.horizontal} />
                  }>
                  <ItemListCard
                    id={`${id}-${itemKey}`}
                    className="ItemCard"
                    item={item}
                    layout={layout}
                    data-testid={getTestId(`item-${itemKey}`)}
                    {...itemCard}
                  />
                </LazyLoadComponent>
              )
            })}
          </ItemListGrid>
        </ItemListWrapper>
      )}

      {showEmptyMessage && shouldShowEmptyMessage && (
        <EmptyMessageWrapper>
          {showEmptyExtraIcon && <SearchEmptyIcon />}
          <Message
            id={`${id}-error-message`}
            styles={{
              height: 'auto'
            }}
            {...emptyMessage}>
            {showActionText && (
              <ActionText value={actionText} onClick={actionFunction} />
            )}
          </Message>
        </EmptyMessageWrapper>
      )}
      {!items?.length && status === ItemStatus.ERROR && errorMessage && (
        <Message id={`${id}-error-message`} {...errorMessage}>
          {showActionText && (
            <ActionText value={actionText} onClick={actionFunction} />
          )}
        </Message>
      )}
      {status === ItemStatus.LOADING && (
        <Message id={`${id}-loading-message`} {...loadingMessage}>
          {showActionText && (
            <ActionText value={actionText} onClick={actionFunction} />
          )}
        </Message>
      )}
    </>
  )
}

export default ItemList
